import { Button, Input, Modal, Select, message, notification } from "antd";
import { useTranslation } from "react-i18next";
import { ApiService, ViewRequestApprover } from "../api";
import ButtonGroup from "antd/es/button/button-group";
import { NCALayerClient } from "ncalayer-js-client";
import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import ReactSignatureCanvas from "react-signature-canvas";
import { openNotificationWithIcon } from "../constants";
import EgovMobileSignModal from "./EgovMobileSignModal";

const userInfo = localStorage.getItem("userInfo")
  ? JSON.parse(localStorage.getItem("userInfo") || "{}")
  : null;

type ActionColumnProps = {
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
};

type DigitalSignColumnProps = ActionColumnProps & {
  // Opens the eGov Mobile (SIGEX QR) signing modal. NCALayer stays on onApprove.
  onEgovMobile: () => void;
};

type EhsActionColumnProps = {
  onApprove: (sign: string | null) => Promise<void>;
  onReject: () => Promise<void>;
};

const DigitalSignActionColumn = (props: DigitalSignColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await props.onApprove();
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  return (
    <ButtonGroup>
      <Button
        type="primary"
        loading={approving}
        disabled={rejecting}
        onClick={() => handleApprove()}
      >
        {t("Approve by EDS")}
      </Button>
      <Button
        disabled={approving || rejecting}
        onClick={() => props.onEgovMobile()}
      >
        {t("Approve via eGov Mobile")}
      </Button>
      <Button
        loading={rejecting}
        disabled={approving}
        onClick={() => handleReject()}
        danger
      >
        {t("Reject")}
      </Button>
    </ButtonGroup>
  );
};

const ButtonActionColumn = (props: ActionColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await props.onApprove();
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  return (
    <ButtonGroup>
      <Button
        type="primary"
        loading={approving}
        disabled={rejecting}
        onClick={() => handleApprove()}
      >
        {t("Approve")}
      </Button>
      <Button
        loading={rejecting}
        disabled={approving}
        onClick={() => handleReject()}
        danger
      >
        {t("Reject")}
      </Button>
    </ButtonGroup>
  );
};

const EHSActionColumn = (props: EhsActionColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [visible, setVisible] = useState(false);
  const sigCanvas = useRef<ReactSignatureCanvas>(null);

  const handleSignClear = () => {
    if (sigCanvas === null) {
      return;
    }
    sigCanvas.current!.clear();
  };

  const handleApprove = async () => {
    if (sigCanvas === null) {
      return;
    }
    setApproving(true);
    const sign = sigCanvas.current!.getTrimmedCanvas().toDataURL("image/png");
    console.log(sign);
    await props.onApprove(sign);
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  const handleOpenModal = () => {
    setVisible(true);
  };

  const handleCancelModal = () => {
    setVisible(false);
  };

  return (
    <div>
      <ButtonGroup>
        <Button
          type="primary"
          loading={approving}
          disabled={rejecting}
          onClick={() => handleOpenModal()}
        >
          {t("Approve by EHS")}
        </Button>
        <Button
          loading={rejecting}
          disabled={approving}
          onClick={() => handleReject()}
          danger
        >
          {t("Reject")}
        </Button>
        <Modal
          title={t("EHS sign")}
          open={visible}
          onOk={handleApprove}
          confirmLoading={approving}
          onCancel={handleCancelModal}
          footer={[
            <Button key="clear" onClick={handleSignClear}>
              {t("Clear")}
            </Button>,
            <Button key="cancel" onClick={handleCancelModal}>
              {t("Cancel")}
            </Button>,
            <Button key="ok" type="primary" onClick={handleApprove}>
              {t("Sign")}
            </Button>,
          ]}
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              width: 468,
              height: 200,
              className: "sigCanvas",
              style: {
                border: "0.5px solid black",
                borderRadius: "5px",
                borderColor: "grey",
              },
            }}
          />
        </Modal>
      </ButtonGroup>
    </div>
  );
};

type ApprovalButtonsColumnProps = {
  record: ViewRequestApprover;
  requestId?: string;
  workflowId: number;
  fetch: number;
  setFetch: (fetch: number) => void;
  // The request's stage layout + where it currently is, so "Return for
  // correction" can offer the earlier stages as targets. Empty for a template
  // that isn't organized into stages — the modal then just returns to Draft.
  stages?: any[];
  currentStageKey?: string;
};

const ApprovalButtonsColumn = (props: ApprovalButtonsColumnProps) => {
  const { t } = useTranslation();
  const [egovVisible, setEgovVisible] = useState(false);
  const [reworkVisible, setReworkVisible] = useState(false);
  const [reworkReason, setReworkReason] = useState("");
  const [reworkTarget, setReworkTarget] = useState<string | undefined>(undefined);
  const [reworkLoading, setReworkLoading] = useState(false);
  // Whether this reader may sign this slot is the SERVER's answer (`can_sign`),
  // not an identity test in the browser. It used to be `userInfo.id ===
  // record.user_id`, which is a second copy of a rule the server already owns --
  // and the copy that had never heard of cover (D2), so somebody standing in for
  // an approver was shown their slot with no buttons on it and nothing saying
  // why. The old test survives only as the fallback for a server that predates
  // the field.
  const canSeeApprovalButtons =
    props.record.can_sign ??
    (userInfo.id === props.record.user_id && props.record.status === "Started");

  if (!canSeeApprovalButtons) {
    return null;
  }

  const handleApprove = async (sign: string | null) => {
    try {
      await ApiService.apiMainRequestWorkflowApproveCreate({
        request_id: parseInt(props.requestId!),
        workflow_id: props.workflowId,
        user_id: userInfo.id,
        sign: sign,
        // Names the slot being signed. One user may hold several roles in a
        // stage, and each row here is one of them — without this the server
        // cannot tell which of their signatures this click is for.
        role_id: props.record.role_id,
      });

      openNotificationWithIcon(
        "success",
        t("Request has been successfully approved")
      );
      props.setFetch(props.fetch + 1);
    } catch (e) {
      openNotificationWithIcon("error");
    }
  };

  const handleButtonApprove = async () => {
    await handleApprove(null);
  };

  const handleEhsApprove = async (sign: string | null) => {
    await handleApprove(sign);
  };

  const handleDigitalSignApprover = async () => {
    const ncalayerClient = new NCALayerClient();

    try {
      await ncalayerClient.connect();
    } catch (error: any) {
      alert(`${t("Failed to connect to NCALayer:")} ${error.toString()}`);
      return;
    }

    let base64EncodedSignature;

    const fileData = await ApiService.apiMainGetRequestDocRetrieve(
      parseInt(props.requestId!)
    );

    try {
      base64EncodedSignature = await ncalayerClient.basicsSignCMS(
        NCALayerClient.basicsStoragesAll,
        fileData,
        NCALayerClient.basicsCMSParamsAttached,
        NCALayerClient.basicsSignerSignAny
      );
    } catch (error: any) {
      if (error.canceledByUser) {
        alert(t("Action cancelled by user."));
      }

      alert(error.toString());
      return;
    }

    const signature = Array.isArray(base64EncodedSignature)
      ? base64EncodedSignature[0]
      : base64EncodedSignature;
    await handleApprove(signature);

    return signature;
  };

  const handleReject = async () => {
    try {
      await ApiService.apiMainRequestWorkflowRejectCreate({
        request_id: parseInt(props.requestId!),
        workflow_id: props.workflowId,
        user_id: userInfo.id,
        sign: null,
        role_id: props.record.role_id,
      });

      openNotificationWithIcon(
        "success",
        t("Request has been successfully rejected")
      );

      props.setFetch(props.fetch + 1);
    } catch (e) {
      openNotificationWithIcon("error");
    }
  };

  // Stages the permit can be sent back to: everything strictly before the one
  // it is on. The backend enforces the same rule — rework only moves backwards.
  const stagesList: any[] = props.stages ?? [];
  const currentIndex = stagesList.findIndex(
    (s) => s.key === props.currentStageKey
  );
  const reworkTargets =
    currentIndex > 0 ? stagesList.slice(0, currentIndex) : [];

  // Return-for-rework: send the permit back to an earlier stage for correction
  // with a required reason.
  const handleRework = async () => {
    if (!reworkReason.trim()) {
      message.warning(t("Please enter a reason"));
      return;
    }
    setReworkLoading(true);
    try {
      await ApiService.apiMainRequestWorkflowReworkCreate({
        request_id: parseInt(props.requestId!),
        workflow_id: props.workflowId,
        reason: reworkReason.trim(),
        role_id: props.record.role_id,
        // Undefined = all the way back to the start, the default.
        target_stage_key: reworkTarget,
      });
      openNotificationWithIcon(
        "success",
        t("Request returned for correction")
      );
      setReworkVisible(false);
      setReworkReason("");
      setReworkTarget(undefined);
      props.setFetch(props.fetch + 1);
    } catch {
      openNotificationWithIcon("error");
    }
    setReworkLoading(false);
  };

  const column = (() => {
    switch (props.record.type) {
      case "Button":
        return (
          <ButtonActionColumn
            onApprove={handleButtonApprove}
            onReject={handleReject}
          />
        );
      case "EDS":
        return (
          <>
            <DigitalSignActionColumn
              onApprove={handleDigitalSignApprover}
              onReject={handleReject}
              onEgovMobile={() => setEgovVisible(true)}
            />
            <EgovMobileSignModal
              requestId={props.requestId}
              visible={egovVisible}
              setVisible={setEgovVisible}
              onSigned={(sign) => handleApprove(sign)}
            />
          </>
        );
      case "EHS":
        return (
          <EHSActionColumn
            onApprove={handleEhsApprove}
            onReject={handleReject}
          />
        );
      default:
        return null;
    }
  })();

  if (column === null) {
    return null;
  }

  return (
    <div>
      {/* Signing for somebody else (D2). Said before the buttons rather than
          after the fact: a stand-in who does not realise whose duty this is
          cannot judge whether they should be exercising it, and the permit
          will record their name against it either way. */}
      {props.record.on_behalf_of && (
        <div
          style={{
            marginBottom: 6,
            fontSize: 12,
            color: "#8c6d1f",
            background: "#fffbe6",
            border: "1px solid #ffe58f",
            borderRadius: 4,
            padding: "3px 8px",
          }}
        >
          {t("You are signing for {{name}}", {
            name: props.record.on_behalf_of,
          })}
        </div>
      )}
      {column}
      <div style={{ marginTop: 6 }}>
        <Button
          type="link"
          size="small"
          danger
          style={{ padding: 0 }}
          onClick={() => setReworkVisible(true)}
        >
          {t("Return for correction")}
        </Button>
      </div>
      <Modal
        title={t("Return for correction")}
        open={reworkVisible}
        onOk={handleRework}
        confirmLoading={reworkLoading}
        onCancel={() => setReworkVisible(false)}
        okText={t("Return")}
      >
        {reworkTargets.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>{t("Return to")}</div>
            <Select
              style={{ width: "100%" }}
              value={reworkTarget ?? ""}
              onChange={(v) => setReworkTarget(v || undefined)}
              options={[
                { value: "", label: t("Back to the start (Draft)") },
                ...reworkTargets.map((s: any) => ({
                  value: s.key,
                  label:
                    s.kind === "Approval"
                      ? `${t(s.name)} · ${t("Approval")}`
                      : t(s.name),
                })),
              ]}
            />
          </div>
        )}
        <p>
          {reworkTarget
            ? t(
                "The request will go back to the selected stage, which has to be completed and approved again."
              )
            : t(
                "The request will go back to Draft for correction and will need approval again."
              )}
        </p>
        <Input.TextArea
          rows={3}
          value={reworkReason}
          onChange={(e) => setReworkReason(e.target.value)}
          placeholder={t("Reason (required)")}
        />
      </Modal>
    </div>
  );
};

export default ApprovalButtonsColumn;
